// AVRASTRO AI Content Studio — the `creative_asset` entity and its approval workflow (requirements 06–11;
// docs/content-studio/FEASIBILITY.md §6.4 and §7). Pure and browser-safe: the platform server keeps the assets (large media
// in its blob store, scoped per workspace) and enforces every rule here; the app shows the same states.
//
//   draft → in_review → approved → archived        (in_review → draft: changes asked for; archived → draft: restored)
//   - submitting for review and approving both re-run the deterministic checks (studio-claims.ts); a blocking issue refuses
//   - the approval records the approver, the time and the SHA-256 of the exact content approved
//   - ANY edit of an asset that is in review or approved puts it back to draft: the approval no longer matches the content
//   - only an approved asset whose content still has the approved hash can be attached to a campaign or handed off for
//     publishing; nothing is ever posted or sent by the studio itself
//   - nothing is deleted: «delete» archives (the records and their approvals stay for the audit)
// Why not a synced CRM table: the studio is server-backed by nature (providers, credits, media) and must be enforced on
// the server; the CRM data schema is unchanged (see docs/content-studio/FEASIBILITY.md «Status»).
import type { CheckResult, StudioLang, StudioTopic } from './studio-claims.ts';

export const ASSET_KINDS = ['image', 'video', 'story', 'carousel', 'copy', 'presentation'] as const;
export type AssetKind = typeof ASSET_KINDS[number];
export const ASSET_STATUSES = ['draft', 'in_review', 'approved', 'archived'] as const;
export type AssetStatus = typeof ASSET_STATUSES[number];

/** export sizes of the major platforms (px). 4:5 = Instagram/Facebook feed portrait; banner = LinkedIn / website header */
export const STUDIO_FORMATS = {
  '1:1': { w: 1080, h: 1080, use: 'feed' },
  '4:5': { w: 1080, h: 1350, use: 'feed' },
  '9:16': { w: 1080, h: 1920, use: 'story' },
  '16:9': { w: 1920, h: 1080, use: 'wide' },
  banner: { w: 1584, h: 396, use: 'banner' },
} as const;
export type StudioFormat = keyof typeof STUDIO_FORMATS;
export const FORMAT_KEYS = Object.keys(STUDIO_FORMATS) as StudioFormat[];

export interface Approval { by: string; byName?: string | null; at: string; hash: string; note?: string | null }
export interface ProvenanceModel { kind: 'copy' | 'review' | 'image' | 'video' | 'voice' | 'transcribe' | 'music'; provider: string; model: string; version?: string | null; licence: string; at: string; ref?: string | null }
export interface Provenance {
  /** the asset contains AI-generated media (images, video, voice) */
  aiGenerated: boolean;
  models: ProvenanceModel[];
  /** stock, music and own uploads with their licences (user uploads: «own, declared by the user») */
  media: { kind: 'image' | 'clip' | 'music' | 'logo'; source: string; licence: string; ref: string | null }[];
  /** C2PA / Content Credentials: not produced yet (needs a signing certificate) — the export carries IPTC/XMP marking */
  c2pa: null | { manifestId: string };
  marking: 'iptc-xmp' | 'png-text' | 'mp4-metadata' | null;
}
export const emptyProvenance = (): Provenance => ({ aiGenerated: false, models: [], media: [], c2pa: null, marking: null });

/** the editable content of a creative (texts, template, picture, brand kit choices) */
export interface CreativeContent {
  templateId: string;
  topic: StudioTopic;
  fields: { headline: string; subline: string; body: string; cta: string };
  caption: string;
  hashtags: string;
  script?: string | null;
  /** the picture (blob id in the studio store); null = the template's own background */
  imageRef: string | null;
  /** photo crop focus 0…1 */
  focus?: { x: number; y: number } | null;
  palette?: { primary: string; accent: string; ink: string; paper: string } | null;
  font?: 'sans' | 'serif' | null;
  showLogo: boolean;
  /** the AVRASTRO mark next to the advisor's brand (co-branding) */
  showPlatformMark: boolean;
  labels: { marketing: boolean; ai: boolean };
  /** the advisor's own contact line (from the brand kit) */
  contact?: string | null;
  /** reels: the render job and its outputs (blob ids per format) */
  reel?: { jobId: string | null; outputs: Partial<Record<StudioFormat, string>>; poster?: string | null } | null;
}

export interface ExportBinding { ref: string; sha256: string; approvalHash: string; by: string; at: string }
export interface HistoryEntry { at: string; by: string; action: string; from?: AssetStatus; to?: AssetStatus; note?: string | null }
export interface CreativeAsset {
  id: string;
  workspaceId: string;
  campaignId: string | null;
  kind: AssetKind;
  formats: StudioFormat[];
  status: AssetStatus;
  title: string;
  locale: StudioLang;
  content: CreativeContent;
  /** ledger entries (studio-claims.ts) the creative cites */
  claimRefs: string[];
  approvals: Approval[];
  /** where the rendered media are kept (studio blob store); null until exported or rendered */
  storageRef: string | null;
  /**
   * the export the approver vouched for: its blob, the file's SHA-256 and the approval (content hash) it belongs to. Set
   * only by someone who may approve — directly, or by approving an asset that an assistant's export sent back to review.
   * The hand-off serves `storageRef` only when it is this binding under the current approval.
   */
  exportBinding?: ExportBinding | null;
  provenance: Provenance;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  /** SHA-256 of canonicalAssetContent() as last saved */
  hash: string;
  checks: CheckResult | null;
  /** the AI reviewer's ADVISORY opinion (never a substitute for the approval) */
  review: { pass: boolean; problems: { quote?: string; rule?: string; fix?: string }[]; at: string; model: string } | null;
  history: HistoryEntry[];
}

/**
 * a stable JSON of everything an approval covers (keys sorted): the server hashes it with SHA-256. The storage reference
 * (where the rendering is kept) is not content; an export is covered separately by `exportBinding` (see exportIsApproved):
 * an approver's export keeps the approval, anyone else's sends the asset back to review.
 */
export function canonicalAssetContent(a: Pick<CreativeAsset, 'kind' | 'formats' | 'title' | 'locale' | 'content' | 'claimRefs'>): string {
  const sort = (v: unknown): unknown => (Array.isArray(v) ? v.map(sort) : v && typeof v === 'object' ? Object.fromEntries(Object.keys(v as object).sort().filter((k) => (v as Record<string, unknown>)[k] !== undefined).map((k) => [k, sort((v as Record<string, unknown>)[k])])) : v);
  return JSON.stringify(sort({ kind: a.kind, formats: [...a.formats].sort(), title: a.title, locale: a.locale, content: a.content, claimRefs: [...a.claimRefs].sort() }));
}

export type AssetAction = 'submit' | 'approve' | 'reject' | 'archive' | 'restore';
export class StudioError extends Error {
  code: string;
  constructor(code: string) { super(code); this.code = code; }
}
/** who may approve: the advisor or the administrator of the workspace — never an assistant (requirement 11) */
export const canApprove = (role: string) => role === 'admin' || role === 'advisor';
/** who may create and edit creatives (managers read only, as elsewhere in the CRM) */
export const canEditStudio = (role: string) => role === 'admin' || role === 'advisor' || role === 'assistant';

const NEXT: Record<AssetAction, { from: readonly AssetStatus[]; to: AssetStatus }> = {
  submit: { from: ['draft'], to: 'in_review' },
  approve: { from: ['in_review'], to: 'approved' },
  reject: { from: ['in_review'], to: 'draft' },
  archive: { from: ['draft', 'in_review', 'approved'], to: 'archived' },
  restore: { from: ['archived'], to: 'draft' },
};

/**
 * One step of the workflow. `checks` are the deterministic checks of the CURRENT content (the caller recomputes them);
 * `hash` the content's current hash. Returns the asset after the step (a new object).
 */
export function transitionAsset(a: CreativeAsset, action: AssetAction, o: { actor: string; actorName?: string | null; role: string; now: string; checks: CheckResult | null; hash: string; note?: string | null }): CreativeAsset {
  const rule = NEXT[action];
  if (!rule) throw new StudioError('action');
  if (!rule.from.includes(a.status)) throw new StudioError(`status_${a.status}`);
  if (action === 'approve' && !canApprove(o.role)) throw new StudioError('approver_role');
  if (action !== 'archive' && action !== 'reject' && action !== 'restore' && !canEditStudio(o.role)) throw new StudioError('permission');
  if ((action === 'submit' || action === 'approve') && (!o.checks || !o.checks.ok)) throw new StudioError('checks_failed');
  if (action === 'approve' && o.hash !== a.hash) throw new StudioError('hash_mismatch');
  const approvals = action === 'approve' ? [...a.approvals, { by: o.actor, byName: o.actorName ?? null, at: o.now, hash: o.hash, note: o.note ?? null }] : a.approvals;
  return { ...a, status: rule.to, approvals, checks: o.checks ?? a.checks, updatedAt: o.now, version: a.version + 1, history: [...a.history, { at: o.now, by: o.actor, action, from: a.status, to: rule.to, note: o.note ?? null }] };
}

/** an edit of the content: an asset in review or approved goes back to draft (its approval no longer matches) */
export function editAsset(a: CreativeAsset, patch: Partial<Pick<CreativeAsset, 'title' | 'formats' | 'content' | 'claimRefs' | 'campaignId' | 'storageRef' | 'provenance' | 'kind' | 'locale'>>, o: { actor: string; now: string; hash: (x: CreativeAsset) => string }): CreativeAsset {
  if (a.status === 'archived') throw new StudioError('status_archived');
  const next: CreativeAsset = { ...a, ...patch, content: patch.content ? { ...a.content, ...patch.content, fields: { ...a.content.fields, ...(patch.content.fields ?? {}) }, labels: { ...a.content.labels, ...(patch.content.labels ?? {}) } } : a.content };
  const hash = o.hash(next);
  const changed = hash !== a.hash;
  const reset = changed && (a.status === 'approved' || a.status === 'in_review');
  return {
    ...next, hash, status: reset ? 'draft' : a.status, updatedAt: o.now, version: a.version + 1, checks: changed ? null : a.checks, review: changed ? null : a.review,
    history: [...a.history, { at: o.now, by: o.actor, action: reset ? 'edit_reset' : 'edit', ...(reset ? { from: a.status, to: 'draft' as AssetStatus } : {}) }],
  };
}

/** the approval that covers the content as it is now (null when none) */
export function currentApproval(a: Pick<CreativeAsset, 'status' | 'approvals' | 'hash'>): Approval | null {
  if (a.status !== 'approved') return null;
  const last = a.approvals[a.approvals.length - 1];
  return last && last.hash === a.hash ? last : null;
}
/** requirement 11: only approved creatives (still exactly as approved) can be attached to a campaign or published */
export const isAttachable = (a: Pick<CreativeAsset, 'status' | 'approvals' | 'hash'>) => !!currentApproval(a);
/** the recorded export is the one an approver bound to the CURRENT approval (the only file a hand-off may serve) */
export function exportIsApproved(a: Pick<CreativeAsset, 'status' | 'approvals' | 'hash' | 'storageRef' | 'exportBinding'>, sha256: string | null): boolean {
  const ap = currentApproval(a), b = a.exportBinding;
  return !!ap && !!b && !!a.storageRef && b.ref === a.storageRef && b.approvalHash === ap.hash && b.sha256 === sha256;
}
